import type { ChangePoint } from "@repo/api";
import type { DefinitionVersionHistoryEntry } from "@repo/data-store";
import { createContext, use } from "react";
import type { ShellForm } from "./use-shell-form";

/** The shell's status vocabulary — three values, whatever the resource calls them. */
export type DefinitionStatus = "active" | "retired" | "draft";

/**
 * The shell-level fields EVERY definition has, whatever its resourceType.
 *
 * Deliberately not FHIR-shaped: exams are Questionnaire, locations are
 * Location, protocols are PlanDefinition. Each kind maps its resource into
 * this, so the shell never learns the union and does not grow a prop per kind.
 */
export interface DefinitionShell {
  /** "Protocol" | "Exam" | "Device" — the badge text, and the only kind name the chrome knows. */
  kindLabel: string;
  title: string;
  description?: string;
  /** Already collapsed for display. The kind's own code lives on the shell form. */
  status: DefinitionStatus;
  version?: string;
  lastUpdated?: string;
}

export interface CatalogDefinitionState {
  /**
   * `edit` means a builder session with a Save button. A detail page whose
   * fields persist as you type is still `read` — the page reads as a page,
   * and WhenEditing must not mount a builder on it.
   */
  mode: "read" | "edit";
  shell: DefinitionShell;
  canEdit: boolean;
  /**
   * The definition's revision lineage, when it has one. Undefined for a kind
   * that is a live resource rather than a versioned definition — a Location —
   * and that absence is what decides whether the toolbar offers the history
   * button at all. An empty array still offers it; the sheet says so.
   */
  versions?: readonly DefinitionVersionHistoryEntry[];
  /**
   * The instant this page resolved, when it is not the present.
   *
   * Passed in rather than read off the route, because the shell is
   * presentational — and because the version cannot substitute for it: a lineage
   * that was never revised shows the same version number at every instant, so
   * only this says whether the page is showing history.
   */
  asOf?: string;
  /**
   * The instants at which the graph under this definition resolves differently.
   * A superset of the version history in the cases that matter: a revision of a
   * CHILD is one of these without being a version of this definition at all.
   */
  changePoints?: readonly ChangePoint[];
}

export interface CatalogDefinitionContextValue {
  state: CatalogDefinitionState;
  /**
   * Owns exactly three fields: title, description, status. Null on a read
   * page that shows those as plain text; present on one that edits in place.
   * Kind-specific pickers bind to the KIND's own form, not this one.
   */
  form: ShellForm | null;
}

export const CatalogDefinitionContext = createContext<CatalogDefinitionContextValue | null>(null);

/**
 * Read the surrounding definition shell. Throws rather than falling back:
 * a slot rendered outside a Root would otherwise silently render nothing,
 * which is the kind of bug that ships.
 */
export function useCatalogDefinition(slotName: string): CatalogDefinitionContextValue {
  const value = use(CatalogDefinitionContext);
  if (!value) {
    throw new Error(
      `CatalogDefinition.${slotName} was rendered outside a CatalogDefinition.Root or CatalogDefinition.EditRoot.`,
    );
  }
  return value;
}

/**
 * The shell's one status vocabulary. Every not-active, not-draft code — FHIR's
 * retired, inactive, suspended, unknown — reads as Inactive, because the
 * distinction is the kind's business and the badge's job is at-a-glance.
 */
export function statusLabel(status: string): "Active" | "Inactive" | "Draft" {
  if (status === "active") return "Active";
  if (status === "draft") return "Draft";
  return "Inactive";
}

/** `toShell` helpers use this so the collapse is written once, not per kind. */
export function toDefinitionStatus(status: string | undefined): DefinitionStatus {
  if (status === "active") return "active";
  if (status === "draft") return "draft";
  return "retired";
}
